import {anythingDifferent} from "set-state-compare/build/diff-utils"
import Config from "../config.js"
import {dig, digg} from "diggerize"
import {Animated, Dimensions, Easing, PanResponder, Platform, Pressable, ScrollView, TextInput, View} from "react-native"
import React, {createRef, memo, useEffect} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import debounce from "debounce"
import FontAwesomeIcon from "react-native-vector-icons/FontAwesome"
import idForComponent from "@kaspernj/api-maker/build/inputs/id-for-component"
import nameForComponent from "@kaspernj/api-maker/build/inputs/name-for-component"
import Text from "@kaspernj/api-maker/build/utils/text"
import Option from "./option"
import OptionGroup from "./option-group"
import PaginationPageButton from "./pagination-page-button"
import PropTypes from "prop-types"
import propTypesExact from "prop-types-exact"
import RenderHtml from "react-native-render-html"
import {Portal} from "conjointment"
import useEventListener from "ya-use-event-listener"
import usePressOutside from "outside-eye/build/use-press-outside"

const styles = {}
const MOBILE_OPTIONS_MAX_WIDTH = 768

/**
 * @typedef {object} HayaSelectToggleOption
 * @property {string} icon
 * @property {string} label
 * @property {string} value
 */

/**
 * @typedef {object} HayaSelectOption
 * @property {string|number} value
 * @property {import("react").ReactNode} [text]
 * @property {function(): import("react").ReactNode} [content]
 * @property {function(): import("react").ReactNode} [currentContent]
 * @property {boolean} [disabled]
 * @property {string} [html]
 * @property {import("react").ReactNode} [right]
 *
 * Additional option props are allowed; HayaSelect only uses the keys above.
 */

/**
 * @typedef {object} HayaSelectOptionsResult
 * @property {Array<HayaSelectOption>} options
 * @property {number} [totalCount]
 * @property {number} [page]
 * @property {number} [pageSize]
 */

/**
 * @typedef {object} HayaSelectProps
 * @property {string} [attribute]
 * @property {string} [className]
 * @property {boolean} [closeOnChange]
 * @property {object} [defaultToggled]
 * @property {string|number} [defaultValue]
 * @property {Array<string|number>} [defaultValues]
 * @property {Array<HayaSelectOption>} [defaultValuesFromOptions]
 * @property {boolean} [debug]
 * @property {import("react").ReactNode} [id]
 * @property {object} [model]
 * @property {"auto"|"always"|"never"} [mobileOptionsMode]
 * @property {boolean} [multiple]
 * @property {string} [name]
 * @property {function(): import("react").ReactNode} [noOptionsText]
 * @property {function(import("react").SyntheticEvent=): void} [onBlur]
 * @property {function(import("react").SyntheticEvent=): void} [onChange]
 * @property {function(Array<string|number>=): void} [onChangeValue]
 * @property {function(import("react").SyntheticEvent=): void} [onFocus]
 * @property {function({options: Array}): void} [onOptionsClosed]
 * @property {function({options: Array}): void} [onOptionsLoaded]
 * @property {function(object): import("react").ReactNode} [optionContent]
 * @property {Array<HayaSelectOption>|function(): (Array<HayaSelectOption>|HayaSelectOptionsResult)} options
 * @property {boolean} [optionsAbsolute]
 * @property {boolean} [optionsPortal]
 * @property {number} [optionsWidth]
 * @property {import("react").ReactNode} [placeholder]
 * @property {string} [selectedBackgroundColor]
 * @property {string} [selectedHoverBackgroundColor]
 * @property {boolean} [search]
 * @property {import("react-native").TextInputProps} [searchTextInputProps]
 * @property {object} [styles]
 * @property {object} [toggled]
 * @property {Array<HayaSelectToggleOption>} [toggleOptions]
 * @property {boolean} [transparent]
 * @property {Array<string|number>} [values]
 */

/**
 * @typedef {object} HayaSelectState
 * @property {Array<HayaSelectOption>} currentOptions
 * @property {HayaSelectLayout|null} selectContainerLayout
 * @property {HayaSelectLayout|null} endOfSelectLayout
 * @property {number|null} height
 * @property {Array<HayaSelectOption>|undefined} loadedOptions
 * @property {number} loadOptionsAppliedRequestId
 * @property {number} loadOptionsRequestId
 * @property {number} page
 * @property {boolean} pageInputFocused
 * @property {string} pageInputValue
 * @property {number|null} pageSize
 * @property {boolean} opened
 * @property {HayaSelectLayout|null} optionsContainerLayout
 * @property {"above"|"below"|"sheet"|undefined} optionsPlacement
 * @property {number|undefined} optionsTop
 * @property {"hidden"|"visible"|undefined} optionsVisibility
 * @property {number|undefined|null} optionsWidth
 * @property {number|null} scrollLeft
 * @property {number|null} scrollTop
 * @property {number|null} totalCount
 * @property {Record<string|number, string>} toggled
 */

/**
 * @typedef {object} HayaSelectLayout
 * @property {number} [top]
 * @property {number} [left]
 * @property {number} [x]
 * @property {number} [y]
 * @property {number} [width]
 * @property {number} [height]
 */

/**
 * Normalizes React Native and React Native Web layout keys.
 * @param {HayaSelectLayout|null|undefined} layout Native layout.
 * @returns {HayaSelectLayout} Normalized layout.
 */
function normalizeLayout(layout) {
  const normalizedLayout = Object.assign({}, layout)

  if (typeof normalizedLayout.left != "number" && typeof normalizedLayout.x == "number") {
    normalizedLayout.left = normalizedLayout.x
  }

  if (typeof normalizedLayout.top != "number" && typeof normalizedLayout.y == "number") {
    normalizedLayout.top = normalizedLayout.y
  }

  return normalizedLayout
}

/**
 * Checks whether a layout has usable absolute-ish position.
 * @param {HayaSelectLayout|null|undefined} layout Layout to inspect.
 * @returns {boolean} True when top and left can be used.
 */
function layoutHasPosition(layout) {
  return Number.isFinite(layout?.left) && Number.isFinite(layout?.top)
}

/** @returns {boolean} True for native iOS and iOS/iPadOS Safari on web. */
function isIOSLikePlatform() {
  if (Platform.OS == "ios") return true
  if (Platform.OS != "web" || typeof navigator == "undefined") return false

  const platform = navigator.platform || ""
  const userAgent = navigator.userAgent || ""

  return /iPad|iPhone|iPod/.test(platform) ||
    /iPad|iPhone|iPod/.test(userAgent) ||
    (platform == "MacIntel" && navigator.maxTouchPoints > 1)
}

/**
 * @typedef {object} HayaSelectOptionRenderContext
 * @property {string|undefined} icon
 * @property {"current"|"option"} mode
 * @property {HayaSelectOption} option
 * @property {boolean} selected
 * @property {HayaSelectToggleOption|undefined} toggleOption
 * @property {string|undefined} toggleValue
 * @property {Record<string|number, string>} toggled
 */

/**
 * @typedef {object} HayaSelectOnChangePayload
 * @property {import("react").SyntheticEvent} event
 * @property {Array<HayaSelectOption>} options
 * @property {Record<string|number, string>} toggles
 */

/**
 * @typedef {object} HayaSelectStylingContext
 * @property {boolean} opened
 * @property {"above"|"below"|"sheet"|undefined} optionsPlacement
 * @property {HayaSelectState} state
 * @property {Record<string, any>} style
 */

/**
 * @param {ShapeComponent<HayaSelectProps, HayaSelectState>} component
 * @returns {string}
 */
const nameForComponentWithMultiple = (component) => {
  let name = nameForComponent(component)

  const currentOptions = component.getCurrentOptions()
  const values = component.getValues()

  const hasMultipleValues = Array.isArray(values) && values.length > 0

  if (component.props.multiple && name && (currentOptions.length > 0 || hasMultipleValues)) {
    name += "[]"
  }

  return name
}

/** @augments {ShapeComponent<HayaSelectProps, HayaSelectState>} */
class HayaSelect extends ShapeComponent {
  static defaultProps = {
    closeOnChange: false,
    debug: false,
    mobileOptionsMode: "auto",
    multiple: false,
    noOptionsText: null,
    onBlur: null,
    onFocus: null,
    optionsAbsolute: true,
    optionsPortal: true,
    optionsWidth: null,
    search: false,
    searchTextInputProps: undefined,
    transparent: false
  }

  static propTypes = propTypesExact({
    attribute: PropTypes.string,
    className: PropTypes.string,
    closeOnChange: PropTypes.bool.isRequired,
    defaultToggled: PropTypes.object,
    defaultValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    defaultValues: PropTypes.array,
    defaultValuesFromOptions: PropTypes.array,
    debug: PropTypes.bool.isRequired,
    id: PropTypes.node,
    model: PropTypes.object,
    mobileOptionsMode: PropTypes.oneOf(["auto", "always", "never"]),
    multiple: PropTypes.bool.isRequired,
    name: PropTypes.string,
    noOptionsText: PropTypes.func,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onChangeValue: PropTypes.func,
    onFocus: PropTypes.func,
    onOptionsClosed: PropTypes.func,
    onOptionsLoaded: PropTypes.func,
    optionContent: PropTypes.func,
    options: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.shape({
        content: PropTypes.func,
        currentContent: PropTypes.func,
        disabled: PropTypes.bool,
        html: PropTypes.string,
        right: PropTypes.node,
        text: PropTypes.node,
        value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired
      })),
      PropTypes.func
    ]).isRequired,
    optionsAbsolute: PropTypes.bool.isRequired,
    optionsPortal: PropTypes.bool.isRequired,
    optionsWidth: PropTypes.number,
    placeholder: PropTypes.node,
    selectedBackgroundColor: PropTypes.string,
    selectedHoverBackgroundColor: PropTypes.string,
    search: PropTypes.bool.isRequired,
    searchTextInputProps: PropTypes.object,
    styles: PropTypes.object,
    toggled: PropTypes.object,
    toggleOptions: PropTypes.arrayOf(PropTypes.shape({
      icon: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    })),
    transparent: PropTypes.bool.isRequired,
    values: PropTypes.array
  })

  callOptionsPositionAboveIfOutsideScreen = false
  bodyScrollLocked = false
  endOfSelectRef = createRef()
  latestLoadOptionsRequestId = 0
  mobileOptionsBackdropOpacity = new Animated.Value(0)
  optionsContainerRef = createRef()
  pageInputRef = createRef()
  previousBodyOverflow = undefined
  previousDocumentOverflow = undefined
  mobileOptionsContainerProgress = new Animated.Value(0)
  mobileOptionsContainerScale = this.mobileOptionsContainerProgress.interpolate({
    inputRange: [0, 1],
    outputRange: [0.96, 1]
  })
  mobileOptionsContainerTranslateY = this.mobileOptionsContainerProgress.interpolate({
    inputRange: [0, 1],
    outputRange: [20, 0]
  })
  mobileOptionsContainerTransform = [
    {translateY: this.mobileOptionsContainerTranslateY},
    {scale: this.mobileOptionsContainerScale}
  ]
  mobileOptionsClosing = false
  optionGroupStyleEntries = () => {
    const optionGroup = dig(this, "props", "styles", "optionGroup")
    const optionGroupText = dig(this, "props", "styles", "optionGroupText")

    return this.cache("optionGroupStyleEntries", {optionGroup, optionGroupText}, [optionGroup, optionGroupText])
  }
  optionGroupStylingContextKey = () => {
    const {optionGroup, optionGroupText} = this.tt.optionGroupStyleEntries()
    if (typeof optionGroup != "function" && typeof optionGroupText != "function") return null

    return this.cache("optionGroupStylingContextKey", {}, [
      this.s.currentOptions,
      this.s.endOfSelectLayout,
      this.s.height,
      this.s.loadedOptions,
      this.s.loadOptionsAppliedRequestId,
      this.s.loadOptionsRequestId,
      this.s.opened,
      this.s.optionsContainerLayout,
      this.s.optionsPlacement,
      this.s.optionsTop,
      this.s.optionsVisibility,
      this.s.optionsWidth,
      this.s.page,
      this.s.pageInputFocused,
      this.s.pageInputValue,
      this.s.pageSize,
      this.s.scrollLeft,
      this.s.scrollTop,
      this.s.selectContainerLayout,
      this.s.toggled,
      this.s.totalCount
    ])
  }
  optionGroupStylingFor = (stylingName, style = {}, caches = []) => this.tt.stylingFor(stylingName, style, caches)
  searchTextValue = ""
  searchTextInputRef = createRef()
  selectContainerRef = createRef()
  t = Config.current().getUseTranslate()().t
  windowWidth = Dimensions.get("window").width
  windowHeight = Dimensions.get("window").height
  /** @type {HayaSelectState} */
  state = {
    currentOptions: this.defaultCurrentOptions(),
    selectContainerLayout: null,
    endOfSelectLayout: null,
    height: null,
    loadedOptions: this.defaultLoadedOptions(),
    loadOptionsAppliedRequestId: 0,
    loadOptionsRequestId: 0,
    page: 1,
    pageInputFocused: false,
    pageInputValue: "1",
    pageSize: null,
    opened: false,
    optionsContainerLayout: null,
    optionsPlacement: undefined,
    optionsTop: undefined,
    optionsVisibility: undefined,
    optionsWidth: undefined,
    scrollLeft: Platform.OS == "web" ? document.documentElement.scrollLeft : null,
    scrollTop: Platform.OS == "web" ? document.documentElement.scrollTop : null,
    totalCount: null,
    toggled: this.defaultToggled()
  }

  /** @returns {boolean} */
  isDebugEnabled = () => Boolean(this.p.debug)

  /**
   * @param {string} functionName
   * @param {Record<string, any>} [details]
   * @returns {void}
   */
  debugLog = (functionName, details = undefined) => {
    if (!this.isDebugEnabled()) return

    const baseDetails = {
      id: this.p.id,
      name: this.p.name
    }

    const mergedDetails = details ? Object.assign({}, baseDetails, details) : baseDetails

    if (mergedDetails && Object.keys(mergedDetails).length > 0) {
      console.log(`[HayaSelect] ${functionName}`, mergedDetails)
    } else {
      console.log(`[HayaSelect] ${functionName}`)
    }
  }

  setup() {
    const {t} = Config.current().getUseTranslate()()

    this.t = t

    if (Array.isArray(this.props.values)) {
      for (const value of this.props.values) {
        if (typeof value == "undefined") {
          throw new Error("HayaSelect: Undefined given as value")
        }
      }
    }

    const windowTarget = Platform.OS == "web" && typeof window != "undefined" ? window : null

    useEventListener(Dimensions, "change", this.tt.onDimensionsChange)
    usePressOutside(this.tt.optionsContainerRef, this.tt.onPressOutsideOptions)
    useEventListener(windowTarget, "resize", this.tt.onAnythingResizedDebounced)
    useEventListener(windowTarget, "scroll", this.tt.onAnythingScrolledDebounced)

    if (this.isDebugEnabled()) this.debugLog("setup", {
      hasControlledValues: "values" in this.props,
      hasControlledToggled: "toggled" in this.props,
      closeOnChange: this.p.closeOnChange,
      multiple: this.p.multiple,
      optionsType: Array.isArray(this.props.options) ? "array" : typeof this.props.options
    })

    useEffect(() => {
      if (this.tt.callOptionsPositionAboveIfOutsideScreen && this.s.optionsContainerLayout) {
        this.callOptionsPositionAboveIfOutsideScreen = false
        this.setOptionsPositionAboveIfOutsideScreen()
      }
    }, [this.tt.callOptionsPositionAboveIfOutsideScreen, this.s.optionsContainerLayout])

    useEffect(() => {
      const currentOptionIds = this.s.currentOptions?.map((currentOption) => currentOption.value)

      if (Array.isArray(this.props.values) && anythingDifferent(currentOptionIds, this.props.values) && typeof this.props.options == "function") {
        this.setCurrentFromGivenValues()
      }
    }, [this.props.values])
  }

  translate(msgID, options) {
    if (msgID.startsWith(".")) {
      return this.t(`haya_select${msgID}`, options)
    } else {
      return this.t(msgID, options)
    }
  }

  /** @returns {Array<HayaSelectOption>} */
  defaultCurrentOptions() {
    const {defaultValue, defaultValues, values} = this.props
    const {options} = this.p

    if (!Array.isArray(options)) return []
    const controlledValues = Array.isArray(values) ? values : []

    return options.filter(({value}) =>
      (defaultValue && value == defaultValue) ||
        (defaultValues && defaultValues.includes(value)) ||
        controlledValues.includes(value)
    )
  }

  /** @returns {Array<HayaSelectOption>|undefined} */
  defaultLoadedOptions() {
    const {options} = this.p

    if (typeof options == "function") {
      return undefined
    } else if (Array.isArray(options)) {
      return options
    }

    throw new Error(`Unknown type of options: ${typeof options}`)
  }

  /** @returns {number} */
  getActivePage = () => this.s.page || 1

  /** @returns {boolean} */
  isMobileOptionsSheet() {
    if (this.p.mobileOptionsMode == "always") return true
    if (this.p.mobileOptionsMode == "never") return false

    return Dimensions.get("window").width <= MOBILE_OPTIONS_MAX_WIDTH
  }

  /**
   * @param {Array<HayaSelectOption>|HayaSelectOptionsResult} result
   * @returns {HayaSelectOptionsResult}
   */
  parseOptionsResult(result) {
    if (Array.isArray(result)) return {options: result}

    if (result && Array.isArray(result.options)) {
      return {
        options: result.options,
        totalCount: result.totalCount,
        page: result.page,
        pageSize: result.pageSize
      }
    }

    throw new Error(`Unknown options result: ${JSON.stringify(result)}`)
  }

  /**
   * @param {object} params
   * @param {Array<HayaSelectOption>} params.options
   * @param {number} [params.page]
   * @param {number} [params.pageSize]
   * @param {number} [params.totalCount]
   * @returns {number|null}
   */
  resolvePageSize({options, page, pageSize, totalCount}) {
    if (Number.isFinite(pageSize) && pageSize > 0) return pageSize

    if (Number.isFinite(this.s.pageSize) && this.s.pageSize > 0 && page != 1) {
      return this.s.pageSize
    }

    if (Number.isFinite(totalCount) && Array.isArray(options) && options.length > 0) {
      return options.length
    }

    return Number.isFinite(this.s.pageSize) && this.s.pageSize > 0 ? this.s.pageSize : null
  }

  /** @returns {Record<string|number, string>} */
  defaultToggled() {
    return ("toggled" in this.props) ? this.p.toggled : this.props.defaultToggled || {}
  }

  /** @returns {string} */
  portalName() {
    return nameForComponentWithMultiple(this) || `haya-select-${String(idForComponent(this))}`
  }

  /** @returns {Record<string|number, string>} */
  getToggled = () => ("toggled" in this.props) ? this.p.toggled : this.s.toggled

  /** @returns {Array<string|number>} */
  getValues = () => ("values" in this.props)
    ? (Array.isArray(this.p.values) ? this.p.values : [])
    : (Array.isArray(this.s.currentOptions) ? this.s.currentOptions : []).map((currentOption) => currentOption.value)

  /** @returns {Array<HayaSelectOption|{value: string|number}>} */
  getCurrentOptions = () => {
    if ("values" in this.props && typeof this.props.values != "undefined") {
      if (Array.isArray(this.p.values) && this.p.values.length === 0) return []

      if (Array.isArray(this.props.options) && Array.isArray(this.p.values)) {
        /** @type {Array<HayaSelectOption>} */
        const result = []

        for (const value of this.p.values) {
          const option = this.p.options.find((option) => option.value == value)

          if (option) result.push(option)
        }

        return result
      } else if (this.s.loadedOptions && Array.isArray(this.p.values)) {
        /** @type {Array<HayaSelectOption>} */
        const result = []

        for (const value of this.p.values) {
          const option =
            this.s.loadedOptions.find((option) => option.value == value) ||
            this.s.currentOptions.find((option) => option.value == value)

          if (option) result.push(option)
        }

        return result
      } else if (typeof this.props.options == "function") {
        // Options haven't been loaded yet.
      } else if (Array.isArray(this.p.values)) {
        return this.p.values.map((value) => ({value}))
      }
    }

    return this.s.currentOptions
  }

  /** @returns {Array<string|number>} */
  getCurrentOptionValues() {
    if ("values" in this.props) {
      return Array.isArray(this.p.values) ? this.p.values : []
    }

    const currentOptions = this.getCurrentOptions()

    if (!currentOptions) return []

    return currentOptions
      .map((option) => option?.value)
      .filter((value) => typeof value != "undefined")
  }

  componentDidMount() {
    const {attribute, defaultValue, defaultValues, defaultValuesFromOptions, model, options} = this.props

    if (this.isDebugEnabled()) this.debugLog("componentDidMount", {
      hasAttributeModel: Boolean(attribute && model),
      hasDefaultValues: Boolean(defaultValue || defaultValues || defaultValuesFromOptions),
      optionsType: typeof options
    })

    if (((defaultValue || defaultValues || defaultValuesFromOptions) || (attribute && model)) && typeof options == "function") {
      this.loadDefaultValuesFromOptionsCallback()
    }
  }

  componentDidUpdate() {
    const newState = {}

    if ("toggled" in this.props) {
      const newToggled = this.p.toggled

      if (anythingDifferent(this.state.toggled, newToggled)) {
        newState.toggled = newToggled
      }
    }

    if (Object.keys(newState).length > 0) {
      if (this.isDebugEnabled()) this.debugLog("componentDidUpdate", {syncingKeys: Object.keys(newState)})
      this.s.toggled = newState.toggled
    }

    this.syncOptionsPlacementWithMode()
  }

  /** @returns {void} */
  componentWillUnmount() {
    this.mobileOptionsBackdropOpacity.stopAnimation()
    this.mobileOptionsContainerProgress.stopAnimation()
    this.unlockBodyScroll()
  }

  /** @returns {import("react").ReactNode} */
  render() {
    const {endOfSelectRef} = this.tt
    const {transparent} = this.p
    const {className, placeholder, toggleOptions} = this.props
    const values = Array.isArray(this.props.values) ? this.props.values : []
    const {opened, optionsPlacement} = this.s
    const currentOptions = this.getCurrentOptions()
    const id = idForComponent(this)
    const mobileOptionsSheet = this.isMobileOptionsSheet()

    const selectContainerStyleActual = {...this.stylingFor("selectContainer", {
      flexDirection: "row",
      alignItems: "center",
      backgroundColor: transparent ? undefined : "#fff",
      borderColor: transparent ? undefined : "#999",
      borderRadius: transparent ? undefined : 4,
      borderWidth: transparent ? undefined : 1,
      color: "#000",
      cursor: Platform.OS == "web" ? "pointer" : undefined,
      paddingTop: 5,
      paddingBottom: 5,
      paddingLeft: 5
    }, [transparent])}

    if (opened && !mobileOptionsSheet) {
      // Prevent select from changing size once the content is replaced with search text once opened
      selectContainerStyleActual.height = this.s.height

      const baseBorderRadius = selectContainerStyleActual.borderRadius

      if (optionsPlacement == "above") {
        selectContainerStyleActual.borderTopLeftRadius = 0
        selectContainerStyleActual.borderTopRightRadius = 0
        selectContainerStyleActual.borderBottomRightRadius = baseBorderRadius
        selectContainerStyleActual.borderBottomLeftRadius = baseBorderRadius

        delete selectContainerStyleActual.borderRadius
      } else if (optionsPlacement == "below") {
        selectContainerStyleActual.borderTopLeftRadius = baseBorderRadius
        selectContainerStyleActual.borderTopRightRadius = baseBorderRadius
        selectContainerStyleActual.borderBottomRightRadius = 0
        selectContainerStyleActual.borderBottomLeftRadius = 0

        delete selectContainerStyleActual.borderRadius
      }
    }

    return (
      <View
        dataSet={this.cache("rootViewDataSet", {
          appliedRequestId: this.s.loadOptionsAppliedRequestId,
          class: className,
          component: "haya-select",
          id,
          opened,
          optionsPlacement,
          requestId: this.s.loadOptionsRequestId,
          toggles: Boolean(toggleOptions)
        }, [this.s.loadOptionsAppliedRequestId, className, id, opened, optionsPlacement, this.s.loadOptionsRequestId, Boolean(toggleOptions)])}
        style={this.stylingFor("main")}
        testID="haya-select"
      >
        <Pressable
          onLayout={this.tt.onSelectContainerLayout}
          onPress={this.tt.onSelectClicked}
          ref={this.tt.selectContainerRef}
          style={selectContainerStyleActual}
          testID="haya-select/select-container"
        >
          <View
            style={this.stylingFor("currentSelected", this.currentSelectedStyle ||= {flex: 1, flexWrap: "wrap", overflow: "hidden"})}
            testID="haya-select/current-selected"
          >
            {opened && !mobileOptionsSheet &&
              this.searchTextInput()
            }
            {(!opened || mobileOptionsSheet) &&
              <>
                {currentOptions.length == 0 &&
                  <Text numberOfLines={1} style={this.stylingFor("nothingSelected", this.nothingSelectedStyle ||= {color: "grey"})}>
                    {placeholder || this.translate(".nothing_selected")}
                  </Text>
                }
                {currentOptions.length == 0 && Platform.OS == "web" &&
                  <>
                    {values.length > 0 &&
                      (this.p.multiple ? values : [values[0]]).map((value) => (
                        <input
                          id={idForComponent(this)}
                          key={`current-value-${value}`}
                          name={nameForComponentWithMultiple(this)}
                          type="hidden"
                          value={value}
                        />
                      ))
                    }
                    {values.length == 0 &&
                      <input
                        id={idForComponent(this)}
                        name={nameForComponentWithMultiple(this)}
                        type="hidden"
                        value=""
                      />
                    }
                  </>
                }
                {currentOptions.map((currentOption) =>
                  <View
                    key={currentOption.key || `current-value-${currentOption.value}`}
                    style={this.stylingFor("currentOption", {marginRight: 6})}
                    testID="haya-select/current-option"
                  >
                    {currentOption.type == "group" &&
                      <View style={this.stylingFor("currentOptionGroup", this.currentOptionGroupStyle ||= {fontWeight: "bold"})}>
                        <Text style={this.stylingFor("currentOptionGroupText")}>
                          {currentOption.text}
                        </Text>
                      </View>
                    }
                    {currentOption.type != "group" &&
                      <>
                        {Platform.OS == "web" && nameForComponentWithMultiple(this) &&
                          <input
                            id={idForComponent(this)}
                            name={nameForComponentWithMultiple(this)}
                            type="hidden"
                            value={digg(currentOption, "value")}
                          />
                        }
                        {this.presentOption(currentOption, "current")}
                      </>
                    }
                  </View>
                )}
              </>
            }
          </View>
          <View
            style={this.stylingFor("chevronContainer", this.chevronContainerStyle ||= {
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              marginLeft: "auto",
              marginRight: 8
            })}
            testID="haya-select/chevron-container"
          >
            <FontAwesomeIcon name={opened ? "chevron-up" : "chevron-down"} style={this.stylingFor("chevron", this.chevronStyle ||= {fontSize: 12})} />
          </View>
        </Pressable>
        <View
          onLayout={this.tt.onEndOfSelectLayout}
          ref={endOfSelectRef}
          testID="haya-select/end-of-select"
        />
        {opened && this.p.optionsPortal &&
          <Portal name={this.portalName()}>
            {this.optionsContainer()}
          </Portal>
        }
        {opened && !this.p.optionsPortal &&
          <View>
            {this.optionsContainer()}
          </View>
        }
      </View>
    )
  }

  /**
   * @param {{mobileOptionsSheet?: boolean}} [params]
   * @returns {import("react").ReactNode}
   */
  searchTextInput({mobileOptionsSheet = false} = {}) {
    const iosLikeMobileSheet = mobileOptionsSheet && isIOSLikePlatform()

    return (
      <TextInput
        defaultValue={this.searchTextValue}
        onChangeText={this.tt.onChangeSearchText}
        placeholder={this.translate(".search_dot_dot_dot")}
        ref={this.tt.searchTextInputRef}
        style={this.stylingFor("searchTextInput", styles[`searchTextInput-${iosLikeMobileSheet}`] ||= {
          width: "100%",
          borderWidth: 0,
          fontSize: iosLikeMobileSheet ? 16 : undefined,
          outline: Platform.OS == "web" ? "none" : undefined,
          padding: 0
        }, [iosLikeMobileSheet])}
        testID="haya-select/search-input"
        {...this.p.searchTextInputProps}
      />
    )
  }

  /** @returns {Array<string|number>|string|number|undefined} */
  defaultValues () {
    const {attribute, defaultValue, defaultValues, defaultValuesFromOptions, model} = this.props

    if (defaultValuesFromOptions) return defaultValuesFromOptions
    if (defaultValue) return defaultValue
    if (defaultValues) return defaultValues

    if (attribute && model) {
      if (!(attribute in model)) throw new Error(`No such attribute on ${model.modelClassData().name}: ${attribute}`)

      return model[attribute]()
    }
  }

  /** @returns {boolean} */
  isActive() {
    if (this.tt.endOfSelectRef.current) {
      return true
    }

    return false
  }

  /** @returns {Promise<void>} */
  async loadDefaultValuesFromOptionsCallback() {
    const defaultValues = this.defaultValues()

    if (!defaultValues) return

    if (this.isDebugEnabled()) this.debugLog("loadDefaultValuesFromOptionsCallback", {defaultValues})

    const result = await this.props.options({
      searchValue: this.getSearchText(),
      page: this.getActivePage(),
      values: defaultValues
    })

    const {options} = this.parseOptionsResult(result)
    if (this.isDebugEnabled()) this.debugLog("loadDefaultValuesFromOptionsCallback.result", {loadedOptionsCount: options?.length || 0})

    this.s.currentOptions = this.state.currentOptions.concat(options)
  }

  /**
   * @param {{page?: number}} [params]
   * @returns {Promise<void>}
   */
  loadOptions = async ({page} = {}) => {
    const {options} = this.p
    const searchValue = this.getSearchText()
    const requestId = ++this.latestLoadOptionsRequestId
    this.s.loadOptionsRequestId = requestId
    if (this.isDebugEnabled()) this.debugLog("loadOptions", {
      page,
      requestId,
      searchValue,
      optionsType: Array.isArray(options) ? "array" : typeof options
    })

    if (Array.isArray(options)) {
      return this.loadOptionsFromArray(options, searchValue, requestId)
    }

    const requestedPage = Number.isFinite(page) ? page : this.getActivePage()
    const result = await options({searchValue, page: requestedPage})
    const {options: loadedOptions, page: resultPage, pageSize, totalCount} = this.parseOptionsResult(result)

    if (requestId != this.latestLoadOptionsRequestId) {
      if (this.isDebugEnabled()) this.debugLog("loadOptions.ignored_stale_result", {
        requestId,
        latestLoadOptionsRequestId: this.latestLoadOptionsRequestId,
        requestedPage,
        searchValue
      })

      return
    }

    const resolvedPage = Number.isFinite(resultPage) ? resultPage : requestedPage
    const resolvedPageSize = this.resolvePageSize({options: loadedOptions, page: resolvedPage, pageSize, totalCount})
    const totalPages = Number.isFinite(totalCount) && Number.isFinite(resolvedPageSize) && resolvedPageSize > 0
      ? Math.ceil(totalCount / resolvedPageSize)
      : null

    this.setState({
      loadedOptions,
      loadOptionsAppliedRequestId: requestId,
      page: resolvedPage,
      pageInputValue: String(resolvedPage),
      pageSize: Number.isFinite(totalCount) ? resolvedPageSize : null,
      totalCount: Number.isFinite(totalCount) ? totalCount : null
    }, () => this.props.onOptionsLoaded?.({options: loadedOptions}))

    if (this.isDebugEnabled()) this.debugLog("loadOptions.result", {
      loadedOptionsCount: loadedOptions?.length || 0,
      resolvedPage,
      resolvedPageSize,
      totalCount: Number.isFinite(totalCount) ? totalCount : null
    })
  }

  /**
   * @param {{key: string, loadedOption: HayaSelectOption}}
   * @returns {import("react").ReactNode}
   */
  hayaSelectOption({key, loadedOption}) {
    if (loadedOption.type == "group") {
      return (
        <OptionGroup
          key={key}
          option={loadedOption}
          styleEntries={this.tt.optionGroupStyleEntries()}
          stylingContextKey={this.tt.optionGroupStylingContextKey()}
          stylingFor={this.tt.optionGroupStylingFor}
        />
      )
    }

    return (
      <Option
        currentOptionValues={this.getCurrentOptionValues()}
        icon={this.iconForOption(loadedOption)}
        key={key}
        option={loadedOption}
        onOptionClicked={this.tt.onOptionClicked}
        optionsPlacement={this.s.optionsPlacement}
        presentOption={this.tt.presentOption}
        selectedBackgroundColor={this.props.selectedBackgroundColor}
        selectedHoverBackgroundColor={this.props.selectedHoverBackgroundColor}
      />
    )
  }

  /**
   * @param {Array<HayaSelectOption>} options
   * @param {string} [searchValue]
   * @param {number} [requestId]
   * @returns {void}
   */
  loadOptionsFromArray(options, searchValue, requestId = this.latestLoadOptionsRequestId) {
    const lowerSearchValue = searchValue?.toLowerCase()
    const loadedOptions = options.filter(({text}) => !lowerSearchValue || text?.toLowerCase()?.includes(lowerSearchValue))
    if (this.isDebugEnabled()) this.debugLog("loadOptionsFromArray", {
      requestId,
      totalOptionsCount: options.length,
      searchValue,
      loadedOptionsCount: loadedOptions.length
    })

    this.s.loadedOptions = loadedOptions
    this.s.loadOptionsAppliedRequestId = requestId
    this.s.page = 1
    this.s.pageInputValue = "1"
    this.s.pageSize = null
    this.s.totalCount = null
  }

  /**
   * @param {{window: {width: number, height: number}}} event
   * @returns {void}
   */
  onDimensionsChange = ({window}) => {
    this.windowWidth = window.width
    this.windowHeight = window.height

    if (this.s.opened) {
      this.syncOptionsPlacementWithMode()
    }
  }

  /**
   * @param {{nativeEvent?: {layout?: HayaSelectLayout}}} e
   * @returns {void}
   */
  onSelectContainerLayout = (e) => {
    this.s.selectContainerLayout = normalizeLayout(digg(e, "nativeEvent", "layout"))
    this.measureNativeLayout(this.tt.selectContainerRef, "selectContainerLayout")
  }

  /**
   * @param {{nativeEvent?: {layout?: HayaSelectLayout}}} e
   * @returns {void}
   */
  onEndOfSelectLayout = (e) => {
    const endOfSelectLayout = normalizeLayout(digg(e, "nativeEvent", "layout"))
    const newState = {endOfSelectLayout}

    if (this.s.opened && endOfSelectLayout?.width) {
      newState.optionsWidth = endOfSelectLayout.width
    }

    this.setState(newState, () => {
      this.measureNativeLayout(this.tt.endOfSelectRef, "endOfSelectLayout")

      if (this.s.opened && this.s.optionsContainerLayout) {
        this.setOptionsPositionAboveIfOutsideScreen()
      }
    })
  }

  /**
   * @param {{nativeEvent?: {layout?: HayaSelectLayout}}} e
   * @returns {void}
   */
  onOptionsContainerLayout = (e) => {
    this.s.optionsContainerLayout = normalizeLayout(digg(e, "nativeEvent", "layout"))
  }

  /** @returns {void} */
  measureNativeSelectLayouts() {
    this.measureNativeLayout(this.tt.selectContainerRef, "selectContainerLayout")
    this.measureNativeLayout(this.tt.endOfSelectRef, "endOfSelectLayout")
  }

  /**
   * Measures a native view in window coordinates for portal positioning.
   * @param {import("react").RefObject<object>} ref View ref.
   * @param {"selectContainerLayout"|"endOfSelectLayout"} stateKey State layout key.
   * @returns {void}
   */
  measureNativeLayout(ref, stateKey) {
    if (Platform.OS == "web") return

    const element = ref.current
    if (!element || typeof element.measureInWindow != "function") return

    element.measureInWindow((left, top, width, height) => {
      const measuredLayout = {height, left, top, width}

      if (!layoutHasPosition(measuredLayout)) return

      /** @type {Partial<HayaSelectState>} */
      const newState = {
        [stateKey]: measuredLayout
      }

      if (stateKey == "endOfSelectLayout" && this.s.opened && width) {
        newState.optionsWidth = width
      }

      if (!anythingDifferent(this.s[stateKey], measuredLayout)) {
        return
      }

      this.setState(newState, () => {
        if (this.s.opened && this.s.optionsContainerLayout) {
          this.setOptionsPositionAboveIfOutsideScreen()
        }
      })
    })
  }

  /**
   * @param {import("react").SyntheticEvent} e
   * @returns {void}
   */
  onSelectClicked = (e) => {
    e.preventDefault()
    e.stopPropagation()

    const {opened} = this.s
    if (this.isDebugEnabled()) this.debugLog("onSelectClicked", {opened})

    if (opened) {
      this.closeOptions()
    } else {
      this.openOptions()
    }
  }

  onSearchTextInputChangedDebounced = debounce(this.tt.loadOptions, 200)

  /**
   * @param {{options?: Array<HayaSelectOption>}} [params]
   * @returns {void}
   */
  closeOptions({options} = {}) {
    const closedOptions = options || this.getCurrentOptions()
    if (this.isDebugEnabled()) this.debugLog("closeOptions", {closedOptionsCount: closedOptions?.length || 0})

    if (this.s.opened && this.s.optionsPlacement == "sheet") {
      this.closeMobileOptionsWithAnimation({closedOptions})
      return
    }

    this.finishCloseOptions({closedOptions})
  }

  /**
   * @param {{closedOptions: Array<HayaSelectOption>}} params Close payload.
   * @returns {void}
   */
  closeMobileOptionsWithAnimation({closedOptions}) {
    if (this.mobileOptionsClosing) return

    this.mobileOptionsClosing = true
    this.mobileOptionsBackdropOpacity.stopAnimation()
    this.mobileOptionsContainerProgress.stopAnimation()

    Animated.parallel([
      Animated.timing(this.mobileOptionsBackdropOpacity, {
        duration: 90,
        easing: Easing.out(Easing.cubic),
        toValue: 0,
        useNativeDriver: true
      }),
      Animated.timing(this.mobileOptionsContainerProgress, {
        duration: 110,
        easing: Easing.in(Easing.cubic),
        toValue: 0,
        useNativeDriver: true
      })
    ]).start(() => {
      this.mobileOptionsClosing = false
      this.finishCloseOptions({closedOptions})
    })
  }

  /**
   * @param {{closedOptions: Array<HayaSelectOption>}} params Close payload.
   * @returns {void}
   */
  finishCloseOptions({closedOptions}) {
    this.callOptionsPositionAboveIfOutsideScreen = false
    this.unlockBodyScroll()

    this.setState(
      {
        height: null,
        loadedOptions: undefined,
        opened: false,
        optionsContainerLayout: null,
        optionsVisibility: "hidden",
        page: 1,
        pageInputFocused: false,
        pageInputValue: "1",
        pageSize: null,
        totalCount: null
      },
      () => {
        if (this.isDebugEnabled()) this.debugLog("closeOptions.done", {opened: this.s.opened, optionsVisibility: this.s.optionsVisibility})
      }
    )

    if (this.props.onOptionsClosed) {
      this.props.onOptionsClosed({options: closedOptions})
    }

    if (this.p.onBlur) this.p.onBlur()
  }

  /** @returns {void} */
  prepareMobileOptionsAnimation() {
    this.mobileOptionsClosing = false
    this.mobileOptionsBackdropOpacity.stopAnimation()
    this.mobileOptionsContainerProgress.stopAnimation()
    this.mobileOptionsBackdropOpacity.setValue(0)
    this.mobileOptionsContainerProgress.setValue(0)
  }

  /** @returns {void} */
  startMobileOptionsOpenAnimation() {
    Animated.parallel([
      Animated.timing(this.mobileOptionsBackdropOpacity, {
        duration: 90,
        easing: Easing.out(Easing.cubic),
        toValue: 1,
        useNativeDriver: true
      }),
      Animated.timing(this.mobileOptionsContainerProgress, {
        duration: 130,
        easing: Easing.out(Easing.back(1.05)),
        toValue: 1,
        useNativeDriver: true
      })
    ]).start()
  }

  /** @returns {string} */
  getSearchText = () => this.searchTextValue || ""

  /** @returns {void} */
  resetSearchTextInput = () => {
    this.searchTextValue = ""
    const input = this.tt.searchTextInputRef.current

    if (input?.clear) {
      input.clear()
    } else if (input?.setNativeProps) {
      input.setNativeProps({text: ""})
    } else if (input) {
      try {
        input.value = ""
      } catch (error) {
        // Ignore if the ref doesn't support direct value assignment.
      }
    }
  }

  /**
   * @param {string} searchText
   * @returns {void}
   */
  onChangeSearchText = (searchText) => {
    if (this.isDebugEnabled()) this.debugLog("onChangeSearchText", {searchText, currentPage: this.s.page})
    this.searchTextValue = searchText

    if (this.s.page != 1) {
      this.setState({page: 1, pageInputValue: "1"}, this.tt.onSearchTextInputChangedDebounced)
    } else {
      this.tt.onSearchTextInputChangedDebounced()
    }
  }

  /** @returns {void} */
  openOptions() {
    const mobileOptionsSheet = this.isMobileOptionsSheet()

    if (this.isDebugEnabled()) this.debugLog("openOptions", {
      currentOptionsCount: this.getCurrentOptions()?.length || 0,
      mobileOptionsSheet,
      searchEnabled: this.p.search
    })
    this.searchTextValue = ""
    this.callOptionsPositionAboveIfOutsideScreen = !mobileOptionsSheet

    if (mobileOptionsSheet) {
      this.prepareMobileOptionsAnimation()
    }

    this.setState(
      {
        height: mobileOptionsSheet ? null : this.s.selectContainerLayout?.height,
        opened: true,
        optionsPlacement: mobileOptionsSheet ? "sheet" : "below",
        optionsVisibility: mobileOptionsSheet ? "visible" : "hidden",
        optionsWidth: mobileOptionsSheet ? undefined : this.s.endOfSelectLayout?.width,
        page: 1,
        pageInputFocused: false,
        pageInputValue: "1",
        scrollLeft: Platform.OS == "web" ? document.documentElement.scrollLeft : null,
        scrollTop: Platform.OS == "web" ? document.documentElement.scrollTop : null
      },
      () => {
        if (mobileOptionsSheet) {
          this.lockBodyScroll()
          this.startMobileOptionsOpenAnimation()
        } else {
          this.measureNativeSelectLayouts()
          this.focusTextInput()
        }

        this.resetSearchTextInput()
        this.loadOptions({page: 1})
      }
    )

    if (this.p.onFocus) this.p.onFocus()
  }

  /** @returns {void} */
  focusTextInput = () => digg(this.tt.searchTextInputRef, "current")?.focus()

  /** @returns {void} */
  lockBodyScroll() {
    if (Platform.OS != "web" || typeof document == "undefined" || this.bodyScrollLocked) return

    this.previousBodyOverflow = document.body?.style.overflow
    this.previousDocumentOverflow = document.documentElement?.style.overflow

    if (document.body) document.body.style.overflow = "hidden"
    if (document.documentElement) document.documentElement.style.overflow = "hidden"

    this.bodyScrollLocked = true
  }

  /** @returns {void} */
  unlockBodyScroll() {
    if (Platform.OS != "web" || typeof document == "undefined" || !this.bodyScrollLocked) return

    if (document.body) document.body.style.overflow = this.previousBodyOverflow || ""
    if (document.documentElement) document.documentElement.style.overflow = this.previousDocumentOverflow || ""

    this.previousBodyOverflow = undefined
    this.previousDocumentOverflow = undefined
    this.bodyScrollLocked = false
  }

  /** @returns {void} */
  setOptionsPosition() {
    if (!this.isActive()) {
      return // Debounce after un-mount handeling.
    }

    if (this.isMobileOptionsSheet()) {
      this.setOptionsPositionSheet()
      return
    }

    this.unlockBodyScroll()

    if (this.isDebugEnabled()) this.debugLog("setOptionsPosition")
    this.callOptionsPositionAboveIfOutsideScreen = true
    this.setOptionsPositionBelow()
  }

  /** @returns {void} */
  syncOptionsPlacementWithMode() {
    if (!this.s.opened) return

    if (this.isMobileOptionsSheet()) {
      if (this.s.optionsPlacement != "sheet") this.setOptionsPositionSheet()
      return
    }

    if (this.s.optionsPlacement == "sheet") {
      this.setOptionsPositionBelow()
      return
    }

    this.unlockBodyScroll()
    this.measureNativeSelectLayouts()
  }

  /** @returns {void} */
  setOptionsPositionAboveIfOutsideScreen() {
    if (!this.s.opened) return
    if (this.isMobileOptionsSheet()) {
      this.setOptionsPositionSheet()
      return
    }

    const {windowHeight} = this.tt
    const {optionsContainerLayout, selectContainerLayout} = this.s
    const endOfSelectLayout = this.s.endOfSelectLayout

    const optionsTop = Platform.OS == "web"
      ? endOfSelectLayout?.top
      : typeof selectContainerLayout?.top == "number" && typeof selectContainerLayout?.height == "number"
        ? selectContainerLayout.top + selectContainerLayout.height + 1
        : undefined

    if (!Number.isFinite(optionsTop)) {
      if (this.isDebugEnabled()) this.debugLog("setOptionsPositionAboveIfOutsideScreen", {placement: "below", reason: "missing-options-top"})
      this.measureNativeSelectLayouts()
      return
    }

    const optionsTotalBottomPosition = optionsContainerLayout.height + optionsTop
    const windowHeightWithScroll = windowHeight + (this.s.scrollTop || 0)

    if (windowHeightWithScroll < optionsTotalBottomPosition) {
      if (this.isDebugEnabled()) this.debugLog("setOptionsPositionAboveIfOutsideScreen", {placement: "above"})
      this.setOptionsPositionAbove()
    } else {
      if (this.isDebugEnabled()) this.debugLog("setOptionsPositionAboveIfOutsideScreen", {placement: "below"})
      this.s.optionsVisibility = "visible"
    }
  }

  /** @returns {void} */
  setOptionsPositionSheet() {
    if (!this.s.opened) return

    const switchingToSheet = this.s.optionsPlacement != "sheet"

    if (switchingToSheet) {
      this.prepareMobileOptionsAnimation()
    }

    this.lockBodyScroll()

    this.setState({
      height: null,
      opened: true,
      optionsPlacement: "sheet",
      optionsVisibility: "visible",
      optionsWidth: undefined
    }, () => {
      if (switchingToSheet) this.startMobileOptionsOpenAnimation()
    })
  }

  /** @returns {void} */
  setOptionsPositionAbove() {
    if (!this.s.opened) return

    const {endOfSelectLayout} = this.s
    if (this.isDebugEnabled()) this.debugLog("setOptionsPositionAbove")
    this.unlockBodyScroll()

    this.setState(
      {
        opened: true,
        optionsPlacement: "above",
        optionsVisibility: "visible",
        optionsWidth: endOfSelectLayout?.width
      },
      () => this.focusTextInput()
    )
  }

  /** @returns {void} */
  setOptionsPositionBelow() {
    if (!this.s.opened) return

    if (this.isDebugEnabled()) this.debugLog("setOptionsPositionBelow")
    this.unlockBodyScroll()
    this.setState(
      {
        opened: true,
        optionsPlacement: "below",
        optionsVisibility: "hidden",
        optionsWidth: this.s.endOfSelectLayout?.width
      },
      () => this.focusTextInput()
    )
  }

  /** @returns {void} */
  onAnythingResized = () => {
    if (this.isDebugEnabled()) this.debugLog("onAnythingResized", {opened: this.s.opened})
    if (this.s.opened) {
      this.setOptionsPosition()
    }
  }

  onAnythingResizedDebounced = debounce(this.tt.onAnythingResized, 25)

  /** @returns {void} */
  onAnythingScrolled = () => {
    if (this.isDebugEnabled()) this.debugLog("onAnythingScrolled", {opened: this.s.opened})
    if (this.s.opened) {
      this.s.scrollLeft = Platform.OS == "web" ? document.documentElement.scrollLeft : null
      this.s.scrollTop = Platform.OS == "web" ? document.documentElement.scrollTop : null
      if (this.isMobileOptionsSheet()) return

      this.setOptionsPosition()
    }
  }

  onAnythingScrolledDebounced = debounce(this.tt.onAnythingScrolled, 25)

  /** @returns {void} */
  onPressOutsideOptions = () => {
    if (this.isDebugEnabled()) this.debugLog("onPressOutsideOptions", {opened: this.s.opened})
    // If options are open and a click is made outside of the options container
    if (this.s.opened) {
      this.closeOptions()
    }
  }

  /**
   * @param {import("react").SyntheticEvent} event Responder event.
   * @returns {void}
   */
  onMobileOptionsBackdropRelease = (event) => {
    event.preventDefault?.()
    event.stopPropagation?.()

    this.closeOptions()
  }

  mobileOptionsBackdropPanResponder = PanResponder.create({
    onStartShouldSetPanResponder: () => true,
    onMoveShouldSetPanResponder: () => false,
    onPanResponderRelease: this.tt.onMobileOptionsBackdropRelease,
    onPanResponderTerminate: this.tt.onMobileOptionsBackdropRelease
  })

  /** @returns {number|null} */
  paginationTotalPages() {
    const {totalCount} = this.s

    if (!Number.isFinite(totalCount) || totalCount <= 0) return null

    const pageSize = this.resolvePageSize({
      options: this.s.loadedOptions || [],
      page: this.getActivePage(),
      pageSize: this.s.pageSize,
      totalCount
    })

    if (!Number.isFinite(pageSize) || pageSize <= 0) return null

    return Math.ceil(totalCount / pageSize)
  }

  /**
   * @param {number} totalPages
   * @returns {Array<{key: string, type: "page"|"ellipsis", value?: number}>}
   */
  paginationPageItems(totalPages) {
    const currentPage = this.getActivePage()
    const items = []
    const addPage = (page) => items.push({key: `page-${page}`, type: "page", value: page})
    const addEllipsis = (key) => items.push({key, type: "ellipsis"})

    if (totalPages <= 7) {
      for (let page = 1; page <= totalPages; page += 1) {
        addPage(page)
      }

      return items
    }

    addPage(1)

    const windowSize = 2
    let start = Math.max(2, currentPage - windowSize)
    let end = Math.min(totalPages - 1, currentPage + windowSize)

    if (currentPage <= 3) {
      start = 2
      end = Math.min(totalPages - 1, 5)
    } else if (currentPage >= totalPages - 2) {
      end = totalPages - 1
      start = Math.max(2, totalPages - 4)
    }

    if (start > 2) addEllipsis("ellipsis-start")

    for (let page = start; page <= end; page += 1) {
      addPage(page)
    }

    if (end < totalPages - 1) addEllipsis("ellipsis-end")

    addPage(totalPages)

    return items
  }

  /**
   * @param {number} totalPages
   * @returns {string}
   */
  paginationDisplayValue(totalPages) {
    const activePage = this.getActivePage()
    const fallbackText = `Page ${activePage} of ${totalPages}`

    return this.translate(".pagination_page_of_pages", {
      defaultValue: fallbackText,
      page: activePage,
      totalPages
    }) || fallbackText
  }

  /**
   * @param {number} totalPages
   * @returns {string}
   */
  paginationInputValue(totalPages) {
    if (this.s.pageInputFocused) return this.s.pageInputValue

    return this.paginationDisplayValue(totalPages)
  }

  /** @param {number} page */
  setPaginationPage = (page) => {
    const totalPages = this.paginationTotalPages()
    if (this.isDebugEnabled()) this.debugLog("setPaginationPage", {requestedPage: page, totalPages})

    if (!totalPages) return

    const nextPage = Math.min(Math.max(Math.floor(page), 1), totalPages)

    if (nextPage == this.getActivePage()) {
      this.s.pageInputValue = String(this.getActivePage())
      return
    }

    this.setState(
      {page: nextPage, pageInputValue: String(nextPage)},
      () => this.tt.loadOptions({page: nextPage})
    )
  }

  /** @param {import("react").SyntheticEvent} event */
  onPaginationPrevPressed = (event) => {
    event.preventDefault?.()
    event.stopPropagation?.()

    this.setPaginationPage(this.getActivePage() - 1)
  }

  /** @param {import("react").SyntheticEvent} event */
  onPaginationNextPressed = (event) => {
    event.preventDefault?.()
    event.stopPropagation?.()

    this.setPaginationPage(this.getActivePage() + 1)
  }

  /** @returns {void} */
  onPaginationInputFocus = () => {
    this.s.pageInputFocused = true
    this.s.pageInputValue = String(this.getActivePage())
  }

  /** @param {string} value */
  onPaginationInputChange = (value) => {
    this.s.pageInputValue = value
  }

  /** @param {import("react").SyntheticEvent} event */
  onPaginationInputBlur = (event) => {
    event.preventDefault?.()
    event.stopPropagation?.()

    const totalPages = this.paginationTotalPages()
    const rawValue = event?.target?.value ?? this.s.pageInputValue
    const parsedValue = rawValue ? Number(String(rawValue).match(/\d+/)?.[0]) : NaN
    const nextPage = Number.isFinite(parsedValue) ? parsedValue : Number(this.s.pageInputValue)

    if (totalPages && Number.isFinite(nextPage)) {
      this.setState({pageInputFocused: false}, () => this.setPaginationPage(nextPage))
      return
    }

    this.setState({
      pageInputFocused: false,
      pageInputValue: String(this.getActivePage())
    })
  }

  /** @param {import("react").SyntheticEvent} event */
  onPaginationInputSubmit = (event) => {
    event.preventDefault?.()
    event.stopPropagation?.()

    const totalPages = this.paginationTotalPages()

    if (!totalPages) {
      this.s.pageInputFocused = false
      this.s.pageInputValue = String(this.getActivePage())
      return
    }

    const nextPage = Number(this.s.pageInputValue)

    if (!Number.isFinite(nextPage)) {
      this.s.pageInputFocused = false
      this.s.pageInputValue = String(this.getActivePage())
      return
    }

    this.setState({pageInputFocused: false}, () => this.setPaginationPage(nextPage))
  }

  /** @param {import("react").SyntheticEvent} event */
  onPaginationInputKeyDown = (event) => {
    if (event?.key !== "Enter") return

    this.tt.onPaginationInputSubmit(event)
  }

  /** @returns {import("react").ReactNode|null} */
  paginationControls() {
    const totalPages = this.paginationTotalPages()

    if (!totalPages || totalPages <= 1) return null

    const currentPage = this.getActivePage()
    const prevDisabled = currentPage <= 1
    const nextDisabled = currentPage >= totalPages

    return (
      <View
        style={styles.paginationContainer ||= {
          borderTopColor: "#e2e8f0",
          borderTopWidth: 1,
          padding: 8
        }}
        testID="haya-select/options-pagination"
      >
        <View
          style={styles.paginationHeader ||= {flexDirection: "row", alignItems: "center", justifyContent: "space-between"}}
          testID="haya-select/pagination-header"
        >
          <Pressable
            disabled={prevDisabled}
            onPress={this.tt.onPaginationPrevPressed}
            style={styles[`paginationNavButton-${prevDisabled}`] ||= {
              alignItems: "center",
              backgroundColor: "#f8fafc",
              borderColor: "#cbd5e1",
              borderRadius: 8,
              borderWidth: 1,
              height: 30,
              justifyContent: "center",
              opacity: prevDisabled ? 0.4 : 1,
              width: 30
            }}
            testID="haya-select/pagination-prev"
          >
            <FontAwesomeIcon
              name="chevron-left"
              style={styles.paginationNavIcon ||= {color: "#334155", fontSize: 12}}
            />
          </Pressable>
          <View
            dataSet={this.cache("paginationLabelDataSet", {
              page: currentPage
            }, [currentPage])}
            style={styles.paginationLabelButton ||= {
              alignItems: "center",
              backgroundColor: "#f1f5f9",
              borderColor: "#cbd5e1",
              borderRadius: 14,
              borderWidth: 1,
              justifyContent: "center",
              minWidth: 140,
              paddingHorizontal: 10,
              paddingVertical: 6
            }}
            testID="haya-select/pagination-label"
          >
            <TextInput
              keyboardType="number-pad"
              onBlur={this.tt.onPaginationInputBlur}
              onChangeText={this.tt.onPaginationInputChange}
              onFocus={this.tt.onPaginationInputFocus}
              onPressIn={this.tt.onPaginationInputFocus}
              onKeyDown={this.tt.onPaginationInputKeyDown}
              onSubmitEditing={this.tt.onPaginationInputSubmit}
              ref={this.tt.pageInputRef}
              selectTextOnFocus
              style={styles.paginationInputStyle ||= {
                borderWidth: 0,
                color: "#0f172a",
                fontSize: 12,
                outline: Platform.OS == "web" ? "none" : undefined,
                padding: 0,
                textAlign: "center",
                width: 120
              }}
              testID="haya-select/pagination-input"
              value={this.paginationInputValue(totalPages)}
            />
          </View>
          <Pressable
            disabled={nextDisabled}
            onPress={this.tt.onPaginationNextPressed}
            style={styles[`paginationNavButton-${nextDisabled}`] ||= {
              alignItems: "center",
              backgroundColor: "#f8fafc",
              borderColor: "#cbd5e1",
              borderRadius: 8,
              borderWidth: 1,
              height: 30,
              justifyContent: "center",
              opacity: nextDisabled ? 0.4 : 1,
              width: 30
            }}
            testID="haya-select/pagination-next"
          >
            <FontAwesomeIcon
              name="chevron-right"
              style={styles.paginationNavIcon ||= {color: "#334155", fontSize: 12}}
            />
          </Pressable>
        </View>
        <View
          style={styles.paginationPages ||= {
            flexDirection: "row",
            flexWrap: "wrap",
            justifyContent: "center",
            marginTop: 8
          }}
          testID="haya-select/pagination-pages"
        >
          {this.paginationPageItems(totalPages).map((item) => {
            if (item.type == "ellipsis") {
              return (
                <View
                  key={item.key}
                  style={styles.paginationEllipsis ||= {paddingHorizontal: 6}}
                  testID="haya-select/pagination-ellipsis"
                >
                  <Text
                    style={styles.paginationEllipsisText ||= {
                      color: "#64748b",
                      fontSize: 12,
                      fontWeight: 600
                    }}
                  >
                    ...
                  </Text>
                </View>
              )
            }

            return (
              <PaginationPageButton
                active={item.value == currentPage}
                key={item.key}
                onPageSelected={this.tt.setPaginationPage}
                page={item.value}
              />
            )
          })}
        </View>
      </View>
    )
  }

  /**
   * @param {{loadedOptions: Array<HayaSelectOption>|undefined}} args Options to render.
   * @returns {import("react").ReactNode}
   */
  optionsListContent({loadedOptions}) {
    return (
      <>
        {loadedOptions?.map((loadedOption) =>
          this.hayaSelectOption({
            key: loadedOption.key || `loaded-option-${loadedOption.value}`,
            loadedOption
          })
        )}
        {loadedOptions?.length === 0 &&
          <View
            style={this.stylingFor("noOptionsContainer", this.noOptionsContainerStyle ||= {
              paddingBottom: 10,
              paddingLeft: 8,
              paddingRight: 8,
              paddingTop: 10
            })}
            testID="haya-select/no-options-container"
          >
            <Text>
              {this.p.noOptionsText ? this.p.noOptionsText() : this.translate(".no_options_found")}
            </Text>
          </View>
        }
      </>
    )
  }

  /**
   * @param {{id: string|number, optionsListContent: import("react").ReactNode, paginationControls: import("react").ReactNode|null}} args Mobile sheet content.
   * @returns {import("react").ReactNode}
   */
  mobileOptionsContainer({id, optionsListContent, paginationControls}) {
    const sheetMaxHeight = Math.round(Dimensions.get("window").height * 0.8)
    let sheetStyle = this.stylingFor("optionsContainer", {
      position: "absolute",
      zIndex: 100000,
      elevation: 100000,
      left: 10,
      right: 10,
      bottom: 0,
      maxHeight: sheetMaxHeight,
      shadowColor: "#0f172a",
      shadowOffset: {height: -12, width: 0},
      shadowOpacity: 0.48,
      shadowRadius: 34,
      boxShadow: Platform.OS == "web"
        ? "0 -28px 72px rgba(15, 23, 42, 0.56), 0 -8px 24px rgba(15, 23, 42, 0.38), 0 0 0 1px rgba(15, 23, 42, 0.12)"
        : undefined,
      backgroundColor: "#fff",
      borderTopLeftRadius: 18,
      borderTopRightRadius: 18,
      opacity: this.mobileOptionsContainerProgress,
      overflow: "hidden",
      transform: this.mobileOptionsContainerTransform,
      visibility: this.s.optionsVisibility
    }, [sheetMaxHeight, this.s.optionsVisibility])

    if ("height" in sheetStyle) {
      sheetStyle = Object.assign({}, sheetStyle)
      delete sheetStyle.height
    }

    return (
      <View
        style={this.stylingFor("mobileOptionsOverlay", styles.mobileOptionsOverlay ||= {
          position: Platform.OS == "web" ? "fixed" : "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          zIndex: 99999,
          elevation: 99999
        })}
        testID="haya-select/mobile-options-overlay"
      >
        <Animated.View
          style={this.stylingFor("mobileOptionsBackdrop", this.mobileOptionsBackdropStyle ||= {
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            backgroundColor: "rgba(15, 23, 42, 0.32)",
            opacity: this.mobileOptionsBackdropOpacity
          })}
          testID="haya-select/mobile-options-backdrop"
          {...this.tt.mobileOptionsBackdropPanResponder.panHandlers}
        />
        <Animated.View
          dataSet={this.cache(
            "mobileOptionsContainerDataSet",
            {id, role: "dialog", optionsPlacement: "sheet", optionsVisibility: this.s.optionsVisibility || "hidden"},
            [id, this.s.optionsVisibility]
          )}
          onLayout={this.tt.onOptionsContainerLayout}
          ref={this.tt.optionsContainerRef}
          style={sheetStyle}
          testID="haya-select/options-container"
        >
          <ScrollView
            contentContainerStyle={this.stylingFor("mobileOptionsScrollContent", styles.mobileOptionsScrollContent ||= {
              flexGrow: 1,
              justifyContent: "flex-end"
            })}
            keyboardShouldPersistTaps="handled"
            nestedScrollEnabled
            style={this.stylingFor("mobileOptionsScrollView", styles.mobileOptionsScrollView ||= {flexGrow: 0, flexShrink: 1, minHeight: 0})}
            testID="haya-select/mobile-options-scroll-view"
          >
            {optionsListContent}
          </ScrollView>
          {paginationControls}
          <View
            style={this.stylingFor("mobileOptionsSearchContainer", styles.mobileOptionsSearchContainer ||= {
              borderTopColor: "#cbd5e1",
              borderTopWidth: 1,
              paddingBottom: 14,
              paddingLeft: 14,
              paddingRight: 14,
              paddingTop: 10
            })}
            testID="haya-select/mobile-options-search-container"
          >
            {this.searchTextInput({mobileOptionsSheet: true})}
          </View>
        </Animated.View>
      </View>
    )
  }

  /** @returns {import("react").ReactNode|null} */
  optionsContainer() {
    const {selectContainerLayout, loadedOptions, endOfSelectLayout, optionsContainerLayout, optionsPlacement, optionsVisibility} = this.s
    let left, top
    const id = idForComponent(this)
    const desktopOptionsPlacement = optionsPlacement == "sheet" ? "below" : optionsPlacement
    const optionsListContent = this.optionsListContent({loadedOptions})
    const paginationControls = this.paginationControls()

    if (this.isMobileOptionsSheet()) {
      return this.mobileOptionsContainer({id, optionsListContent, paginationControls})
    }

    let style = {
      position: "absolute",
      zIndex: 99999,
      elevation: 99999,
      visibility: optionsVisibility,
      width: this.p.optionsWidth || this.s.optionsWidth,
      backgroundColor: "#fff",
      borderColor: "#999",
      borderWidth: 1,
      maxHeight: 300,
      overflow: "hidden"
    }

    if (!this.p.optionsPortal) {
      style.top = 0
      style.left = 0
    } else if (!this.p.optionsAbsolute) {
      style.left = 0
      style.bottom = 0
    } else if (desktopOptionsPlacement == "below") {
      if (Platform.OS == "web") {
        // onLayout top value is sometimes negative so use browser JS to get it instead
        top = digg(this.tt.endOfSelectRef.current.getBoundingClientRect(), "top") + document.documentElement.scrollTop + 1

        // onLayout left values doesn't always update when changed
        left = digg(this.tt.endOfSelectRef.current.getBoundingClientRect(), "left") + document.documentElement.scrollLeft
      } else {
        left = selectContainerLayout?.left
        top = typeof selectContainerLayout?.top == "number" && typeof selectContainerLayout?.height == "number"
          ? selectContainerLayout.top + selectContainerLayout.height + 1
          : undefined
      }

      if (Number.isFinite(left) && Number.isFinite(top)) {
        style.left = left
        style.top = Platform.OS == "web" ? top - 2 : top
      } else {
        style.left = 0
        style.top = 0
        style.visibility = "hidden"
      }
    } else if (desktopOptionsPlacement == "above") {
      if (Platform.OS == "web") {
        // onLayout top value is sometimes negative so use browser JS to get it instead
        top = digg(this.tt.selectContainerRef.current.getBoundingClientRect(), "top") + document.documentElement.scrollTop

        // onLayout left values doesn't always update when changed
        left = digg(this.tt.selectContainerRef.current.getBoundingClientRect(), "left") + document.documentElement.scrollLeft
      } else {
        left = selectContainerLayout?.left
        top = selectContainerLayout?.top
      }

      if (Number.isFinite(left) && Number.isFinite(top)) {
        style.left = left
        style.top = top - optionsContainerLayout.height + 1
      } else {
        style.left = 0
        style.top = 0
        style.visibility = "hidden"
      }
    } else {
      throw new Error(`Unkonwn options placement: ${desktopOptionsPlacement}`)
    }

    if (Platform.OS != "web") {
      style.opacity = optionsVisibility == "hidden" ? 0 : 1
      style.overflow = "hidden"
    }

    style = this.stylingFor("optionsContainer", style, [left, top, style.visibility, style.width])

    return (
      <View
        dataSet={this.cache(
          "optionsContainerDataSet",
          {id, role: "dialog", optionsVisibility: optionsVisibility || "hidden"},
          [id, optionsVisibility]
        )}
        onLayout={this.tt.onOptionsContainerLayout}
        ref={this.tt.optionsContainerRef}
        style={style}
        testID="haya-select/options-container"
      >
        <ScrollView
          keyboardShouldPersistTaps="handled"
          nestedScrollEnabled
          style={styles[`optionsScrollView-${style.maxHeight || 300}`] ||= {
            flexGrow: 0,
            flexShrink: 1,
            maxHeight: style.maxHeight || 300,
            minHeight: 0
          }}
          testID="haya-select/options-scroll-view"
        >
          {optionsListContent}
        </ScrollView>
        {paginationControls}
      </View>
    )
  }

  /**
   * @param {import("react").SyntheticEvent} event
   * @param {HayaSelectOption} loadedOption
   * @returns {void}
   */
  onOptionClicked = (event, loadedOption) => {
    event.preventDefault()
    event.stopPropagation()

    const {onChange, toggleOptions} = this.props
    const {multiple} = this.p
    const currentOptions = this.getCurrentOptions()
    const toggled = this.getToggled()
    const newState = {}
    const existingOption = currentOptions.find((currentOption) => currentOption.value == loadedOption.value)
    const newToggled = {...toggled}
    let newCurrentOptions
    let action

    if (existingOption) {
      if (toggleOptions) {
        const currentToggle = toggled[loadedOption.value]
        const currentIndex = toggleOptions.findIndex((element) => element.value == currentToggle)

        if (currentIndex >= (toggleOptions.length - 1)) {
          // No next toggled - remove toggled and option
          delete newToggled[loadedOption.value]
          action = "remove-option-after-last-toggle"

          newCurrentOptions = currentOptions.filter((currentOption) => currentOption.value != loadedOption.value)
        } else {
          // Already toggled - set to next toggle
          newToggled[loadedOption.value] = digg(toggleOptions, currentIndex + 1, "value")
          action = "cycle-toggle"
        }

        newState.toggled = newToggled
      } else {
        // Remove from current options
        action = "remove-option"
        newCurrentOptions = currentOptions.filter((currentOption) => currentOption.value != loadedOption.value)
      }
    } else {
      // Don't do anything if the clicked option is disabled
      if (loadedOption.disabled) {
        if (this.isDebugEnabled()) this.debugLog("onOptionClicked", {
          action: "ignore-disabled-option",
          optionValue: loadedOption.value
        })
        return
      }

      if (toggleOptions) {
        // Set fresh toggle
        newToggled[loadedOption.value] = toggleOptions[0].value
        newState.toggled = newToggled
      }

      if (multiple || toggleOptions) {
        action = "add-option"
        newCurrentOptions = currentOptions.concat([loadedOption])
      } else {
        action = "replace-single-option"
        newCurrentOptions = [loadedOption]
      }
    }

    if ("values" in this.props && this.props.values !== undefined) {
      // currentOptions are controlled and a useMemo callback is handeling setting current options.
    } else if (newCurrentOptions) {
      newState.currentOptions = newCurrentOptions
    }

    const options = newCurrentOptions || currentOptions
    if (this.isDebugEnabled()) this.debugLog("onOptionClicked", {
      action: action || "toggle-only",
      multiple,
      optionValue: loadedOption.value,
      optionsCountBefore: currentOptions.length,
      optionsCountAfter: options.length
    })

    if (!multiple || this.p.closeOnChange) this.closeOptions({options})

    if (onChange) {
      /** @type {HayaSelectOnChangePayload} */
      onChange({
        event,
        options,
        toggles: newToggled
      })
    }

    if (this.props.onChangeValue) {
      let optionValue

      if (multiple) {
        optionValue = options.map((option) => option.value)
      } else {
        optionValue = dig(options, 0, "value")
      }

      this.p.onChangeValue(optionValue)
    }

    if ("toggled" in newState) {
      this.s.toggled = newState.toggled
    }

    if ("currentOptions" in newState) {
      this.s.currentOptions = newState.currentOptions
    }
  }

  /**
   * @param {string} stylingName
   * @param {Record<string, any>} [style]
   * @param {Array<any>} [caches]
   * @returns {Record<string, any>}
   */
  stylingFor(stylingName, style = {}, caches = []) {
    let customStyling = dig(this, "props", "styles", stylingName)
    const baseStyle = {...style}

    if (typeof customStyling == "function") {
      /** @type {HayaSelectStylingContext} */
      customStyling = customStyling({
        opened: this.s.opened,
        optionsPlacement: this.s.optionsPlacement,
        state: this.state,
        style: baseStyle
      })
    }

    if (customStyling) {
      return Object.assign({}, baseStyle, customStyling)
    }

    return this.cache(`stylingFor-${stylingName}`, style, caches)
  }

  /**
   * @param {HayaSelectOption} option
   * @returns {string|undefined}
   */
  iconForOption(option) {
    const {toggleOptions} = this.props || {}
    const toggled = this.getToggled()

    if (toggleOptions && (option.value in toggled)) {
      const toggledValue = toggled[option.value]
      const toggledOption = toggleOptions.find((element) => element.value == toggledValue)

      if (!toggledOption) {
        throw new Error(`Couldn't find a toggle option for value: ${toggledValue}`)
      }

      return toggledOption.icon
    }
  }

  /**
   * @param {HayaSelectOption} option
   * @param {"current"|"option"} mode
   * @returns {import("react").ReactNode}
   */
  presentOption = (option, mode) => {
    const {optionContent, toggleOptions} = this.props || {}
    const toggled = this.getToggled()
    const icon = this.iconForOption(option)
    const toggleValue = toggled[option.value]
    const toggleOption = toggleOptions?.find((toggleOptionI) => toggleOptionI.value == toggleValue)
    const selected = this.getCurrentOptionValues().some((value) => value == option.value)
    let style
    let contentNode

    if (mode == "current") {
      style = this.stylingFor("currentOptionPresentationText", {flex: 1, whiteSpace: "nowrap"})
    } else {
      style = this.stylingFor("optionPresentationText", {flex: 1, whiteSpace: "nowrap"})
    }

    return (
      <View
        dataSet={this.cache("presentOptionViewDataSet", {
          text: option.text,
          toggleIcon: toggleOption?.icon,
          toggleValue: toggleOption?.value,
          value: option.value
        }, [option.text, option.value, toggleOption?.icon, toggleOption?.value])}
        style={this.cache("optionPresentationStyle", {flexDirection: "row", alignItems: "center"})}
        testID="haya-select/option-presentation"
      >
        {toggleOptions && !(option.value in toggled) &&
          <View
            style={this.cache("toggleIconPlaceholderStyle", {width: 20})}
            testID="haya-select/toggle-icon-placeholder"
          />
        }
        {toggleOptions && (option.value in toggled) &&
          <View style={this.cache("toggleIconContainerStyle", {alignItems: "center", justifyContent: "center", width: 20})}>
            <FontAwesomeIcon
              name={icon}
              testID="haya-select/toggle-icon"
            />
          </View>
        }
        {(() => {
          if (optionContent) {
            /** @type {HayaSelectOptionRenderContext} */
            contentNode = optionContent({icon, mode, option, selected, toggleOption, toggleValue, toggled})
          } else if (mode == "current" && option.currentContent) {
            contentNode = option.currentContent()
          } else if (option.content) {
            contentNode = option.content()
          } else if ("html" in option && Platform.OS != "web") {
            contentNode = <RenderHtml source={{html: digg(option, "html")}} />
          } else if ("html" in option && Platform.OS == "web") {
            contentNode = <div dangerouslySetInnerHTML={{__html: digg(option, "html")}} />
          } else {
            contentNode = (
              <Text
                style={style}
                testID="haya-select/option-presentation-text"
              >
                {option.text}
              </Text>
            )
          }

          return (
            <>
              <View style={this.cache("optionPresentationContentStyle", {flex: 1})}>
                {contentNode}
              </View>
              {option.right &&
                <View style={this.cache("optionPresentationRightStyle", {alignItems: "center", justifyContent: "center", marginLeft: 8})}>
                  {option.right}
                </View>
              }
            </>
          )
        })()}
      </View>
    )
  }

  /** @returns {Promise<void>} */
  async setCurrentFromGivenValues() {
    const {options, values} = this.p

    if (Array.isArray(values) && values.length === 0) {
      if (this.s.currentOptions?.length) {
        this.s.currentOptions = []
      }

      return
    }

    const result = await options({page: this.getActivePage(), values})
    const {options: currentOptions} = this.parseOptionsResult(result)
    const currentValues = currentOptions?.map((currentOption) => currentOption.value)
    const stateValues = this.s.currentOptions?.map((currentOption) => currentOption.value)

    if (anythingDifferent(currentValues, stateValues)) {
      this.s.currentOptions = currentOptions
    }
  }
}

const HayaSelectShapeComponent = shapeComponent(HayaSelect)

export default memo(HayaSelectShapeComponent)
